import { pageHref } from '../blocks/page-link-search.js';
import type { ContextPin, ContextSet, Message } from './types.js';

export type ContextLinkTarget = {
  id: string;
  title: string;
  kind: 'page' | 'database';
  href: string;
};

export type ContextReferenceSpan = {
  start: number;
  end: number;
  title: string;
  href: string;
  withAt: boolean;
};

const INTERNAL_BLOCK_PATH = /^\/(?:page|database)\/[0-9a-f-]{36}\/?$/i;

export function isInternalBlockHref(href: string | undefined | null): href is string {
  if (!href) {
    return false;
  }
  return INTERNAL_BLOCK_PATH.test(href.split('?')[0] ?? '');
}

export function contextLinkTargetFromPin(
  pin: ContextPin,
  resolvedTitle?: string,
): ContextLinkTarget | null {
  if (!pin.blockId || pin.kind === 'file') {
    return null;
  }
  const title = resolvedTitle?.trim() || pin.title?.trim() || 'Untitled';
  const kind = pin.kind === 'database' ? 'database' : 'page';
  return {
    id: pin.blockId,
    title,
    kind,
    href: pageHref({ id: pin.blockId, type: kind }),
  };
}

function pushLinkTarget(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  target: ContextLinkTarget,
): void {
  const key = target.title.trim().toLowerCase();
  if (!key || seenTitles.has(key)) {
    return;
  }
  seenTitles.add(key);
  targets.push({ ...target, title: target.title.trim() });
}

export function buildContextLinkTargets(
  contextSet: ContextSet,
  resolvedTitles?: ReadonlyMap<string, string>,
): ContextLinkTarget[] {
  const targets: ContextLinkTarget[] = [];
  const seenTitles = new Set<string>();
  const seenIds = new Set<string>();

  for (const pin of contextSet.pins) {
    if (!pin.blockId || pin.kind === 'file' || seenIds.has(pin.blockId)) {
      continue;
    }
    seenIds.add(pin.blockId);
    const kind = pin.kind === 'database' ? 'database' : 'page';
    const href = pageHref({ id: pin.blockId, type: kind });
    const resolved = resolvedTitles?.get(pin.blockId)?.trim();
    const pinned = pin.title?.trim();

    if (resolved) {
      pushLinkTarget(targets, seenTitles, {
        id: pin.blockId,
        title: resolved,
        kind,
        href,
      });
    }
    if (pinned) {
      pushLinkTarget(targets, seenTitles, {
        id: pin.blockId,
        title: pinned,
        kind,
        href,
      });
    }
    if (!resolved && !pinned) {
      pushLinkTarget(targets, seenTitles, {
        id: pin.blockId,
        title: 'Untitled',
        kind,
        href,
      });
    }
  }

  for (const root of contextSet.scopeRoots ?? []) {
    if (!root.blockId || seenIds.has(root.blockId)) {
      continue;
    }
    seenIds.add(root.blockId);
    const resolved = resolvedTitles?.get(root.blockId)?.trim();
    const scoped = root.title?.trim();
    const href = pageHref({ id: root.blockId, type: 'page' });
    if (resolved) {
      pushLinkTarget(targets, seenTitles, {
        id: root.blockId,
        title: resolved,
        kind: 'page',
        href,
      });
    }
    if (scoped) {
      pushLinkTarget(targets, seenTitles, {
        id: root.blockId,
        title: scoped,
        kind: 'page',
        href,
      });
    }
    if (!resolved && !scoped) {
      pushLinkTarget(targets, seenTitles, {
        id: root.blockId,
        title: 'Untitled',
        kind: 'page',
        href,
      });
    }
  }

  return targets;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value);
}

function parseToolResult(raw: unknown): Record<string, unknown> | null {
  if (isRecord(raw)) {
    return raw;
  }
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function toolResultFailed(content: Record<string, unknown>): boolean {
  if (content.ok === false) {
    return true;
  }
  const parsed = parseToolResult(content.result);
  return parsed != null && 'error' in parsed;
}

function blockNavKind(type: string | undefined): ContextLinkTarget['kind'] | null {
  if (type === 'database') {
    return 'database';
  }
  if (type === 'page') {
    return 'page';
  }
  return null;
}

function linkTargetFromBlock(
  id: string | undefined,
  title: string | undefined,
  type: string | undefined,
): ContextLinkTarget | null {
  if (!id || !isUuid(id)) {
    return null;
  }
  const kind = blockNavKind(type);
  if (!kind) {
    return null;
  }
  const trimmedTitle = title?.trim();
  return {
    id,
    title: trimmedTitle && trimmedTitle.length > 0 ? trimmedTitle : 'Untitled',
    kind,
    href: pageHref({ id, type: kind }),
  };
}

function collectLinkTarget(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  candidate: ContextLinkTarget | null,
): void {
  if (!candidate) {
    return;
  }
  pushLinkTarget(targets, seenTitles, candidate);
}

function collectFromBlockRecord(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  block: unknown,
): void {
  if (!isRecord(block)) {
    return;
  }
  const id = typeof block.id === 'string' ? block.id : undefined;
  const title = typeof block.title === 'string' ? block.title : undefined;
  const type = typeof block.type === 'string' ? block.type : undefined;
  collectLinkTarget(targets, seenTitles, linkTargetFromBlock(id, title, type));
}

function collectFromSearchResults(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  results: unknown,
): void {
  if (!Array.isArray(results)) {
    return;
  }
  for (const row of results) {
    if (!isRecord(row)) {
      continue;
    }
    const containerId = typeof row.containerId === 'string' ? row.containerId : undefined;
    const containerTitle =
      typeof row.containerTitle === 'string' ? row.containerTitle : undefined;
    const containerType =
      typeof row.containerType === 'string' ? row.containerType : undefined;
    collectLinkTarget(
      targets,
      seenTitles,
      linkTargetFromBlock(containerId, containerTitle, containerType),
    );

    const rowType = typeof row.type === 'string' ? row.type : undefined;
    const rowKind = blockNavKind(rowType);
    if (rowKind) {
      const rowId = typeof row.id === 'string' ? row.id : undefined;
      const rowTitle = typeof row.title === 'string' ? row.title : undefined;
      collectLinkTarget(targets, seenTitles, linkTargetFromBlock(rowId, rowTitle, rowType));
    }
  }
}

function collectFromPageChildren(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  children: unknown,
): void {
  if (!Array.isArray(children)) {
    return;
  }
  for (const child of children) {
    if (!isRecord(child)) {
      continue;
    }
    const id = typeof child.id === 'string' ? child.id : undefined;
    const title = typeof child.title === 'string' ? child.title : undefined;
    const type = typeof child.type === 'string' ? child.type : undefined;
    collectLinkTarget(targets, seenTitles, linkTargetFromBlock(id, title, type));
  }
}

function collectFromRecentChanges(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  changes: unknown,
): void {
  if (!Array.isArray(changes)) {
    return;
  }
  for (const change of changes) {
    if (!isRecord(change)) {
      continue;
    }
    const rowId =
      typeof change.rowId === 'string'
        ? change.rowId
        : typeof change.id === 'string'
          ? change.id
          : undefined;
    const title = typeof change.title === 'string' ? change.title : undefined;
    const blockType =
      typeof change.blockType === 'string'
        ? change.blockType
        : typeof change.type === 'string'
          ? change.type
          : undefined;
    collectLinkTarget(targets, seenTitles, linkTargetFromBlock(rowId, title, blockType));
  }
}

function collectFromRagSources(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  ragSources: unknown,
): void {
  if (!Array.isArray(ragSources)) {
    return;
  }
  for (const source of ragSources) {
    if (!isRecord(source)) {
      continue;
    }
    const containerId = typeof source.containerId === 'string' ? source.containerId : undefined;
    const containerTitle = typeof source.containerTitle === 'string' ? source.containerTitle : undefined;
    const containerType =
      typeof source.containerType === 'string' ? source.containerType : undefined;
    collectLinkTarget(
      targets,
      seenTitles,
      linkTargetFromBlock(containerId, containerTitle, containerType),
    );
  }
}

function collectFromToolArgs(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  toolName: string | null | undefined,
  args: unknown,
): void {
  if (!toolName || !isRecord(args)) {
    return;
  }
  const targetTitle = typeof args.targetTitle === 'string' ? args.targetTitle : undefined;

  switch (toolName) {
    case 'fetch':
    case 'edit_page':
    case 'trash_page':
    case 'read_page':
    case 'read_block':
      collectLinkTarget(
        targets,
        seenTitles,
        linkTargetFromBlock(
          typeof args.id === 'string' ? args.id : undefined,
          targetTitle,
          toolName === 'read_page' || toolName === 'fetch' || toolName === 'edit_page'
            ? 'page'
            : undefined,
        ),
      );
      break;
    case 'query_database':
      collectLinkTarget(
        targets,
        seenTitles,
        linkTargetFromBlock(
          typeof args.databaseId === 'string' ? args.databaseId : undefined,
          targetTitle,
          'database',
        ),
      );
      break;
    default:
      break;
  }
}

function collectFromToolResult(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  toolName: string | null | undefined,
  args: unknown,
  parsed: Record<string, unknown> | null,
): void {
  if (!toolName || !parsed || 'error' in parsed) {
    return;
  }

  switch (toolName) {
    case 'search':
    case 'search_notes':
      collectFromSearchResults(targets, seenTitles, parsed.results);
      break;
    case 'fetch':
    case 'create_page':
    case 'edit_page':
      collectFromBlockRecord(targets, seenTitles, parsed);
      collectFromPageChildren(targets, seenTitles, parsed.children);
      break;
    case 'read_page':
      collectFromBlockRecord(targets, seenTitles, parsed.page);
      collectFromPageChildren(targets, seenTitles, parsed.children);
      break;
    case 'read_block':
      collectFromBlockRecord(targets, seenTitles, parsed.block);
      break;
    case 'query_database': {
      const databaseId =
        isRecord(args) && typeof args.databaseId === 'string' ? args.databaseId : undefined;
      const title = typeof parsed.title === 'string' ? parsed.title : undefined;
      collectLinkTarget(targets, seenTitles, linkTargetFromBlock(databaseId, title, 'database'));
      break;
    }
    case 'list_recent_changes':
      collectFromRecentChanges(targets, seenTitles, parsed.changes);
      break;
    case 'create_block':
      collectFromBlockRecord(targets, seenTitles, parsed.block);
      break;
    case 'create_database_row':
      collectFromBlockRecord(targets, seenTitles, parsed.row);
      break;
    default:
      break;
  }
}

function collectFromDebugTrace(
  targets: ContextLinkTarget[],
  seenTitles: Set<string>,
  debugTrace: unknown,
): void {
  if (!isRecord(debugTrace)) {
    return;
  }
  collectFromRagSources(targets, seenTitles, debugTrace.ragSources);

  const rounds = debugTrace.rounds;
  if (!Array.isArray(rounds)) {
    return;
  }
  for (const round of rounds) {
    if (!isRecord(round) || !Array.isArray(round.toolExecutions)) {
      continue;
    }
    for (const execution of round.toolExecutions) {
      if (!isRecord(execution) || execution.ok === false) {
        continue;
      }
      const toolName = typeof execution.name === 'string' ? execution.name : null;
      const args = execution.args;
      collectFromToolArgs(targets, seenTitles, toolName, args);
      collectFromToolResult(
        targets,
        seenTitles,
        toolName,
        args,
        parseToolResult(execution.result),
      );
    }
  }
}

/** Blocks the agent read, searched, or retrieved — derived from persisted tool/debug messages. */
export function extractLinkTargetsFromMessages(messages: Message[]): ContextLinkTarget[] {
  const targets: ContextLinkTarget[] = [];
  const seenTitles = new Set<string>();

  for (const message of messages) {
    if (message.role === 'assistant') {
      collectFromDebugTrace(targets, seenTitles, message.content.debugTrace);
      continue;
    }

    if (message.role !== 'tool' || toolResultFailed(message.content)) {
      continue;
    }

    const toolName = message.toolName;
    const args = message.content.args;
    collectFromToolArgs(targets, seenTitles, toolName, args);
    collectFromToolResult(
      targets,
      seenTitles,
      toolName,
      args,
      parseToolResult(message.content.result),
    );
  }

  return targets;
}

/** Pins/scope plus conversation history (pins win when titles collide). */
export function buildConversationLinkTargets(
  contextSet: ContextSet,
  messages: Message[],
  resolvedTitles?: ReadonlyMap<string, string>,
): ContextLinkTarget[] {
  const targets = buildContextLinkTargets(contextSet, resolvedTitles);
  const seenTitles = new Set(targets.map((target) => target.title.trim().toLowerCase()));
  for (const target of extractLinkTargetsFromMessages(messages)) {
    pushLinkTarget(targets, seenTitles, target);
  }
  return targets;
}

export function collectConversationBlockIds(
  contextSet: ContextSet,
  messages: Message[],
): string[] {
  const ids = new Set<string>();
  for (const pin of contextSet.pins) {
    if (pin.blockId && isUuid(pin.blockId)) {
      ids.add(pin.blockId);
    }
  }
  for (const root of contextSet.scopeRoots ?? []) {
    if (root.blockId && isUuid(root.blockId)) {
      ids.add(root.blockId);
    }
  }
  for (const target of extractLinkTargetsFromMessages(messages)) {
    ids.add(target.id);
  }
  return [...ids];
}

function referenceBoundary(char: string | undefined): boolean {
  return char === undefined || char === ' ' || char === '\n' || char === '\t' || /[.,!?;:)\]*_]/.test(char);
}

function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

function isInsideMarkdownLink(content: string, start: number, end: number): boolean {
  const before = content.slice(0, start);
  const openBracket = before.lastIndexOf('[');
  const closeBracket = before.lastIndexOf(']');
  const openParen = before.lastIndexOf('(');
  if (openBracket > closeBracket && openParen > closeBracket) {
    return true;
  }
  if (content[start - 1] === '[' || content[end] === ']') {
    return true;
  }
  return false;
}

function findTitleMatch(
  content: string,
  lowerContent: string,
  title: string,
  fromIndex: number,
  withAt: boolean,
): { start: number; end: number } | null {
  const titleLower = title.toLowerCase();
  const prefix = withAt ? '@' : '';
  const needle = `${prefix}${titleLower}`;
  const idx = lowerContent.indexOf(needle, fromIndex);
  if (idx < 0) {
    return null;
  }

  const start = withAt ? idx : idx;
  const end = idx + prefix.length + title.length;
  const charBefore = start > 0 ? content[start - 1] : undefined;
  if (withAt && charBefore !== undefined && !referenceBoundary(charBefore)) {
    return null;
  }
  if (!withAt && charBefore !== undefined && /[A-Za-z0-9_@[]/.test(charBefore)) {
    return null;
  }
  if (!referenceBoundary(content[end])) {
    return null;
  }
  if (isInsideMarkdownLink(content, start, end)) {
    return null;
  }

  return { start, end };
}

/** Find non-overlapping @mentions and bare pinned titles in text. */
export function findContextReferenceSpans(
  content: string,
  targets: ContextLinkTarget[],
): ContextReferenceSpan[] {
  if (!content || targets.length === 0) {
    return [];
  }

  const lowerContent = content.toLowerCase();
  const sorted = [...targets].sort((a, b) => b.title.length - a.title.length);
  const spans: ContextReferenceSpan[] = [];

  for (const target of sorted) {
    for (const withAt of [true, false] as const) {
      let fromIndex = 0;
      while (fromIndex < content.length) {
        const match = findTitleMatch(content, lowerContent, target.title, fromIndex, withAt);
        if (!match) {
          break;
        }
        const overlaps = spans.some((span) =>
          rangesOverlap(span.start, span.end, match.start, match.end),
        );
        if (!overlaps) {
          spans.push({
            start: match.start,
            end: match.end,
            title: content.slice(match.start + (withAt ? 1 : 0), match.end),
            href: target.href,
            withAt,
          });
        }
        fromIndex = match.start + 1;
      }
    }
  }

  return spans.sort((a, b) => a.start - b.start);
}

function applyReferenceMarkdown(content: string, spans: ContextReferenceSpan[]): string {
  if (spans.length === 0) {
    return content;
  }
  const ordered = [...spans].sort((a, b) => b.start - a.start);
  let result = content;
  for (const span of ordered) {
    const label = content.slice(span.start + (span.withAt ? 1 : 0), span.end);
    const replacement = `[${label}](${span.href})`;
    result = `${result.slice(0, span.start)}${replacement}${result.slice(span.end)}`;
  }
  return result;
}

export function linkifyContextReferences(content: string, targets: ContextLinkTarget[]): string {
  return applyReferenceMarkdown(content, findContextReferenceSpans(content, targets));
}

export function prepareChatMarkdown(content: string, targets: ContextLinkTarget[]): string {
  return linkifyContextReferences(content, targets);
}
